import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---------- CSRF double-submit token ----------
// The backend issues a fresh `csrf_token` on login / password change:
//   1. As a non-httpOnly cookie (`csrf_token`) — readable by same-origin JS.
//   2. In the response body — so the token also works when the API and the
//      page live on different origins. In that case the cookie still travels
//      with every request (browser cookie jar), but `document.cookie` on the
//      page origin can't see it, so reading it from the cookie fails.
// We keep the token in memory (per-tab, set from the login response) and
// mirror it to localStorage so it survives page reloads. The interceptor
// resolves it as cookie → localStorage → memory and echoes it as
// `X-CSRF-Token` on every state-changing request; the backend middleware
// (server.py CsrfDoubleSubmitMiddleware) validates it against the cookie via
// hmac.compare_digest.
const CSRF_STORAGE_KEY = "abg_csrf_token";
let csrfMemory = null;

function readCookie(name) {
  try {
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)")
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function readStoredToken() {
  try {
    return localStorage.getItem(CSRF_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setCsrfToken(token) {
  if (!token || typeof token !== "string") return;
  csrfMemory = token;
  try {
    localStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch {
    /* storage unavailable (private mode etc.) — memory is enough */
  }
}

export function clearCsrfToken() {
  csrfMemory = null;
  try {
    localStorage.removeItem(CSRF_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function resolveCsrfToken() {
  // The cookie is the authoritative value the server validates against, so it
  // wins when it's readable (same-origin). localStorage is shared across tabs
  // and therefore fresher than this tab's in-memory token (a login or password
  // change in another tab rotates the token). Memory is the last resort for
  // storage-blocked / private-mode browsing.
  return readCookie("csrf_token") || readStoredToken() || csrfMemory || null;
}

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (["get", "head", "options"].includes(method)) return config;
  const token = resolveCsrfToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers["X-CSRF-Token"] = token;
  } else if (!/\/(auth\/login|contact)$/.test(config.url || "")) {
    // Login is exempt (no session cookie yet) and the contact form is a public
    // endpoint the middleware skips. For everything else, a missing token means
    // the backend will reject the call with 403 CSRF — surface it early so it's
    // diagnosable instead of a silent failure.
    console.warn(
      "[api] state-changing request without a CSRF token — the backend will reject this with 403. " +
        "Log out and back in, or hard-refresh (Ctrl/Cmd+Shift+R) to load the latest bundle."
    );
  }
  return config;
});

// Capture the CSRF token from any response body that carries one (login,
// password change rotate it) so the header stays valid even across origins.
api.interceptors.response.use(
  (response) => {
    const body = response && response.data;
    if (
      body &&
      typeof body === "object" &&
      !Array.isArray(body) &&
      typeof body.csrf_token === "string"
    ) {
      setCsrfToken(body.csrf_token);
    }
    return response;
  },
  (error) => {
    // If the server rejected us specifically on CSRF, our stored token is stale
    // (rotated elsewhere, or storage/cookie out of sync) and will keep failing.
    // Drop it so the next login starts clean instead of looping on 403s.
    const body = error && error.response && error.response.data;
    if (
      error &&
      error.response &&
      error.response.status === 403 &&
      body &&
      body.detail === "CSRF token missing or invalid"
    ) {
      clearCsrfToken();
    }
    return Promise.reject(error);
  }
);

export default api;
